import { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import SectionHeader from "./SectionHeader";
import Drawer from "./Drawer";

const PHOTOS = [
  "archive4.jpg","archive6.jpg","archive7.jpg","archive8.jpg","archive9.jpg",
  "archive11.jpg","archive12.jpg","archive13.jpg","archive14.jpg","archive15.jpg",
  "archive16.jpg","archive17.jpg","archive18.jpg","archive19.jpg","archive20.jpg",
  "archive21.jpg","archive23.jpg","archive24.jpg","archive25.jpg","archive26.jpg",
  "archive27.jpg","archive28.jpg","archive29.jpg","archive30.jpg","archive31.jpg",
  "archive32.jpg","archive33.jpg","archive35.jpg","archive36.jpg","archive37.jpg",
  "archive38.jpg","archive39.jpg","archive40.jpg","archive41.jpg","archive42.jpg",
  "archive43.jpg","archive44.jpg","archive45.jpg","archive46.jpg","archive47.jpg",
  "archive48.jpg","archive49.jpg","archive50.jpg","archive51.jpg","archive52.jpg",
  "archive53.jpg","archive54.jpg","archive55.jpg",
];
const VIDEOS = ["archivevid1.mp4","archivevid2.mp4","archivevid3.mp4","archivevid4.mp4","archivevid5.mp4",
                "archivevid6.mp4","archivevid7.mp4","archivevid8.mp4","archivevid9.mp4","archivevid10.mp4"];

function buildFeed() {
  const out = [{ id: "v-0", type: "video", src: `/assets/archive/videos/${VIDEOS[0]}` }];
  let vi = 1;
  PHOTOS.forEach((p, i) => {
    out.push({ id: `p-${i}`, type: "photo", src: `/assets/archive/photos/${p}` });
    if ((i + 1) % 5 === 0 && vi < VIDEOS.length) {
      out.push({ id: `v-${vi}`, type: "video", src: `/assets/archive/videos/${VIDEOS[vi]}` });
      vi += 1;
    }
  });
  while (vi < VIDEOS.length) {
    const pos = Math.min(out.length - 1, Math.floor((out.length * vi) / VIDEOS.length));
    out.splice(pos, 0, { id: `v-${vi}`, type: "video", src: `/assets/archive/videos/${VIDEOS[vi]}` });
    vi += 1;
  }
  return out;
}
function aspectFor(i, type) {
  if (type === "video") return i % 3 === 0 ? "aspect-[9/16]" : "aspect-video";
  return ["aspect-[3/4]","aspect-square","aspect-[4/5]","aspect-[5/7]"][i % 4];
}

// Video: autoplay muted in viewport, pause offscreen, loop, no play-icon overlay
function VaultVideo({ src }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { e.isIntersecting ? el.play().catch(() => {}) : el.pause(); });
    }, { threshold: 0.35 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return (
    <video ref={ref} src={src} muted loop playsInline preload="metadata"
      className="w-full h-full object-cover" />
  );
}

// Photo: in-place color reveal on tap. No lightbox.
function VaultPhoto({ src }) {
  const [color, setColor] = useState(false);
  return (
    <button
      onClick={() => setColor((c) => !c)}
      className="w-full h-full block relative"
      aria-label={color ? "Show black-and-white" : "Show color"}
    >
      <img
        src={src}
        alt="Philadelphia Freeway archive"
        loading="lazy"
        className={`w-full h-full object-cover transition-all duration-700 ${
          color ? "grayscale-0 scale-[1.01]" : "grayscale"
        }`}
      />
    </button>
  );
}

export default function InTheVault() {
  const FEED = useMemo(buildFeed, []);
  return (
    <section id="vault" data-testid="vault-section" className="relative bg-[#0A0A0A] text-white overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none" />
      <SectionHeader src="/assets/headers/vault.jpg" alt="Enter the Vault" fadeTo="black" />

      <div className="relative px-4 md:px-10 max-w-[1600px] mx-auto pb-20 md:pb-28 -mt-10">
        <div className="mb-10 max-w-3xl relative z-10">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-3">
            Raw & Uncut · 2003
          </div>
          <h2
            data-testid="vault-heading"
            className="font-wide text-white uppercase text-4xl sm:text-5xl md:text-6xl"
          >
            Enter the Vault
          </h2>
          <div className="mt-3 font-mono text-[11px] tracking-[0.35em] uppercase text-white/60">
            Circa 2003
          </div>
          <p className="mt-5 font-body text-white/60 text-base md:text-lg leading-relaxed">
            The Philadelphia Freeway Museum — raw and uncut archives circa 2003.
          </p>
        </div>

        <Drawer id="vault">
          <div
            data-testid="vault-grid"
            className="columns-2 sm:columns-3 lg:columns-4 gap-3 md:gap-4 [column-fill:_balance]"
          >
            {FEED.map((item, i) => (
              <motion.div
                key={item.id}
                data-testid={`vault-item-${item.id}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.4, delay: (i % 4) * 0.04 }}
                className={`group relative w-full mb-3 md:mb-4 overflow-hidden bg-white/5 border border-white/10 break-inside-avoid ${aspectFor(i, item.type)}`}
              >
                {item.type === "video"
                  ? <VaultVideo src={item.src} />
                  : <VaultPhoto src={item.src} />}
              </motion.div>
            ))}
          </div>
        </Drawer>
      </div>
    </section>
  );
}
